import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import VideoCard from '../components/VideoCard';
import { Button } from '../components/ui/button';
import { Loader2, BookmarkPlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Watchlist = () => {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchWatchlist();
  }, [isAuthenticated]);

  const fetchWatchlist = async () => {
    try {
      const response = await axios.get(`${API}/user/watchlist`, getAuthHeader());
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching watchlist:', error);
      toast.error('Failed to load watchlist');
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (videoId) => {
    try {
      await axios.delete(`${API}/user/watchlist/${videoId}`, getAuthHeader());
      setItems(items.filter(item => item.video_id !== videoId));
      toast.success('Removed from watchlist');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-8" data-testid="watchlist-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <BookmarkPlus className="w-10 h-10 text-white" />
          <h1 
            className="text-4xl md:text-5xl font-normal tracking-wide uppercase"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            My Watchlist
          </h1>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-watchlist">
            <BookmarkPlus className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-4">Your watchlist is empty</p>
            <p className="text-gray-600 text-sm mb-6">Browse videos and add them to your watchlist</p>
            <Button onClick={() => navigate('/')} data-testid="browse-btn">
              Browse Videos
            </Button>
          </div>
        ) : (
          <>
            <p className="text-gray-400 mb-6">{items.length} videos in your watchlist</p>
            <div className="video-grid" data-testid="watchlist-grid">
              {items.map((item, index) => (
                <div key={item.video_id || index} className="relative group">
                  <VideoCard 
                    video={{
                      id: item.video_id,
                      title: item.title,
                      thumbnail: item.thumbnail,
                      duration: item.duration,
                      views: '-'
                    }} 
                  />
                  <button
                    onClick={() => removeItem(item.video_id)}
                    className="absolute top-2 left-2 p-2 bg-red-500/80 backdrop-blur rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    data-testid={`remove-watchlist-${item.video_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Watchlist;
